import { normalizedUuidSchema } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useQuoteRequestFormQuery } from '@/entities/quote';
import { useAuthStore } from '@/entities/user';
import { routeForStatus } from '@/features/auth';
import { QuoteRequestForm, QuoteRequestUnavailable } from '@/features/quote-request';
import { myQuoteDetailPath, ONBOARDING_PATH, QUOTE_REQUEST_QTY_PARAM } from '@/shared/config';
import { EyebrowLabel, SitePageShell } from '@/shared/ui';
import { SiteFooter } from '@/widgets/site-footer';
import { SiteHeader } from '@/widgets/site-header';

/** ?qty= 쿼리 — 양의 정수일 때만 처음 수량으로 넘긴다(최소 주문 수량 확인은 폼이 한다). */
const parseQtyParam = (raw: string | null): number | null => {
  if (raw === null || !/^\d+$/.test(raw)) return null;
  const value = Number(raw);
  return Number.isSafeInteger(value) && value > 0 ? value : null;
};

/**
 * 견적 요청 페이지(/request/:designId[?qty=], 견적 설계 §5). 디자인 ID 를 파라미터로, 에디터에서 고른 수량을 쿼리로 받는다
 * (에디터의 견적 요청·내 디자인 목록에서 들어온다). 요청 불가·준비 중이면 폼 대신 안내를 보여 준다.
 * 요청 정보를 받은 뒤에는 다시 조회(원본 디자인 그림 기다림)가 실패해도 폼을 그대로 둔다 — 적던 내용을 잃지 않는다.
 * 머리는 내 견적 상세와 같은 결(작은 영문 머리말·큰 굵은 제목·얇은 아랫선)이고, 본문은 셸 전폭을 쓴다.
 */
export const QuoteRequestPage = (): ReactElement => {
  const { designId = '' } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialQuantity = parseQtyParam(searchParams.get(QUOTE_REQUEST_QTY_PARAM));
  const parsed = normalizedUuidSchema.safeParse(designId);
  const formQuery = useQuoteRequestFormQuery(parsed.success ? parsed.data : '', {
    enabled: parsed.success,
  });
  // 인증 전 계정은 서버(activeProcedure)가 FORBIDDEN 으로 거절한다 — 다시 시도 대신 온보딩으로 보낸다.
  const accountStatus = useAuthStore((state) => state.user?.status);
  const onboardingHref =
    accountStatus !== undefined && routeForStatus(accountStatus) === ONBOARDING_PATH
      ? ONBOARDING_PATH
      : null;

  let body: ReactElement;

  if (!parsed.success) {
    body = (
      // 머리의 얇은 아랫선과 겹치지 않게 아랫선만 둔다.
      <div className="flex flex-col items-center border-b border-foreground/10 py-16 text-center">
        <p role="alert" className="text-sm text-destructive">
          잘못된 디자인 주소입니다. 내 디자인에서 다시 시도해 주세요.
        </p>
      </div>
    );
  } else if (formQuery.data !== undefined) {
    body = (
      <QuoteRequestForm
        form={formQuery.data}
        initialQuantity={initialQuantity}
        onSubmitted={(quoteRequestId) => {
          void navigate(myQuoteDetailPath(quoteRequestId));
        }}
      />
    );
  } else if (formQuery.isError) {
    body = (
      <QuoteRequestUnavailable
        error={formQuery.error}
        onboardingHref={onboardingHref}
        onRetry={() => {
          void formQuery.refetch();
        }}
      />
    );
  } else {
    // 폼과 같은 두 칸 자리표시 — 문구는 화면낭독기에만 읽힌다.
    body = (
      <div>
        <p role="status" className="sr-only">
          견적 요청 정보를 불러오는 중입니다.
        </p>
        <div
          aria-hidden
          className="grid gap-10 lg:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)] lg:gap-12 xl:gap-16"
        >
          <div className="flex flex-col gap-6">
            <div className="h-14 w-full animate-pulse bg-muted/70" />
            <div className="h-72 w-full animate-pulse bg-muted/70" />
          </div>
          <div className="min-h-80 animate-pulse bg-muted/70" />
        </div>
      </div>
    );
  }

  return (
    <SitePageShell header={<SiteHeader />} footer={<SiteFooter />}>
      <div className="flex flex-col gap-10 sm:gap-12">
        <header className="flex flex-col gap-5 border-b border-foreground/10 pb-8 lg:flex-row lg:items-end lg:justify-between lg:gap-16">
          <div className="flex flex-col gap-4">
            <EyebrowLabel>Quote Request</EyebrowLabel>
            <h1 className="text-[clamp(2.25rem,4.5vw,4rem)] leading-none font-bold tracking-[-0.03em]">
              견적 요청
            </h1>
          </div>
          <p className="max-w-md text-sm leading-relaxed text-muted-foreground lg:max-w-xl lg:pb-1 lg:text-right">
            수량과 배송지를 입력하면 임시 견적을 바로 확인하고, 운영자가 검토해 확정 견적서를
            발행합니다.
          </p>
        </header>
        {body}
      </div>
    </SitePageShell>
  );
};
